/**
 * Detail collapse for a program.
 */
import * as React from 'react';
import { FileText, Calendar, Info, Minus, Clock } from 'lucide-react';
import { DetailBadge } from '@/components/common/details/DetailBadge';
import { EmptyPlaceholder } from '@/components/common/details/EmptyPlaceholder';
import { DetailField } from '@/components/common/details/DetailField';
import { DetailsCard } from '@/components/common/DetailsCard';
import {
  PROGRAM_STATUS_CONFIG,
  PROGRAM_TYPE_CONFIG,
  getProgramStatusColor,
  COURSE_PATH_TYPE_CONFIG,
} from '@/components/programs/program-configs';
import { normalizeProgramAvailability } from '@/components/programs/program-form-utils';
import { AvailabilityGrid } from '@/components/forms/AvailabilityGrid';
import type { Program } from '@/types/program.types';

/** Detail collapse sections for a program (header + dates / availability / metadata). */
export const renderProgramDetails = (program: Program): React.ReactNode => {
  const statusConfig = PROGRAM_STATUS_CONFIG[program.status] || PROGRAM_STATUS_CONFIG.active;
  const StatusIcon = statusConfig.icon;
  const typeConfig = program.type
    ? PROGRAM_TYPE_CONFIG[program.type as keyof typeof PROGRAM_TYPE_CONFIG]
    : null;
  const TypeIcon = typeConfig?.icon;
  const coursePathTypeConfig = program.coursePathType
    ? COURSE_PATH_TYPE_CONFIG[program.coursePathType]
    : null;
  const CoursePathTypeIcon = coursePathTypeConfig?.icon;

  const availability = normalizeProgramAvailability(program.availability);

  return (
    <>
      <div className="details-view-header">
        <div className="details-view-header-left">
          <div className="details-view-header-icon">
            <FileText className="w-7 h-7 text-[var(--color-2-nuance-1)]" />
          </div>
          <div className="details-view-header-content">
            <div className="details-view-header-with-badges">
              <h3 className="details-view-header-title">{program.name}</h3>
              <DetailBadge
                label={statusConfig.label}
                icon={StatusIcon}
                colorName={getProgramStatusColor(program.status)}
                variant="filled"
              />
              {typeConfig && TypeIcon && (
                <DetailBadge
                  label={typeConfig.label}
                  icon={TypeIcon}
                  colorName="color-2"
                  variant="outlined"
                />
              )}
              {coursePathTypeConfig && CoursePathTypeIcon && (
                <DetailBadge
                  label={coursePathTypeConfig.label}
                  icon={CoursePathTypeIcon}
                  colorName="color-3"
                  variant="outlined"
                />
              )}
            </div>
            <p className="details-view-header-subtitle">{program.code}</p>
          </div>
        </div>
      </div>

      <div className="details-view-grid">
        <DetailsCard icon={Info} title="Informations générales" colorName="color-2" colSpan={1}>
          <div className="space-y-3">
            <DetailField label="Code" value={program.code} />
            <DetailField label="Nom" value={program.name} />
            {program.description && (
              <DetailField label="Description" value={program.description} vertical />
            )}
          </div>
        </DetailsCard>

        <DetailsCard icon={Calendar} title="Dates" colorName="color-1" colSpan={1}>
          <div className="space-y-3">
            {program.startDate ? (
              <DetailField
                label="Date de début"
                value={new Date(program.startDate).toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                })}
              />
            ) : (
              <EmptyPlaceholder message="Aucune date de début" />
            )}
            {program.endDate ? (
              <DetailField
                label="Date de fin"
                value={new Date(program.endDate).toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                })}
              />
            ) : (
              <EmptyPlaceholder message="Aucune date de fin" />
            )}
          </div>
        </DetailsCard>
      </div>

      {availability.weekly.length > 0 && (
        <div className="details-view-grid">
          <DetailsCard icon={Clock} title="Disponibilités" colorName="color-3" colSpan={2}>
            <AvailabilityGrid value={availability} disabled={true} />
          </DetailsCard>
        </div>
      )}

      {program.createdAt && (
        <div className="mt-8 pt-4 border-t border-[var(--color-1-nuance-3)]">
          <div className="flex items-center gap-6 text-xs text-muted-foreground">
            <span>Créé le {new Date(program.createdAt).toLocaleDateString('fr-FR')}</span>
            {program.updatedAt && (
              <>
                <Minus className="w-3 h-3" />
                <span>Modifié le {new Date(program.updatedAt).toLocaleDateString('fr-FR')}</span>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};
